import React from 'react';

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useAuth } from 'src/common/hooks';
import { IS_MIRAGE } from 'src/common/utils';

import Accounts from './accounts';
import Api from './api';
import CallRescueMonitor from './call-rescue-monitor';
import { Citasmedicas } from './citasmedicas';
import { Navbar, NotFound, PrivateRoute, DotBricksLoading } from './common/components';
import { PhoneSettings } from './phone-settings';
import ProductUpdates from './product-updates';
import ProvisioningRequests from './provisioning-requests';
import Support from './support';
import SurveyConversion from './survey-conversion';
import { Tcr } from './tcr';
import UserProfile from './user-profile';
import 'react-toastify/dist/ReactToastify.min.css';
import WLDevices from './wl-devices';

const TOAST_DURATION_MS = 4000;

const STANDALONE_ROUTE_PATTERNS = [
  '/app/phone-settings/analytics/',
  '/app/phone-settings/reports/',
];

const AuthenticatedApp: React.FC = () => {
  const { isLoading } = useAuth();
  const location = useLocation();

  const isStandaloneView = STANDALONE_ROUTE_PATTERNS.some((pattern) =>
    location.pathname.startsWith(pattern)
  );

  if (isLoading) return <DotBricksLoading />;
  return (
    <div className="flex flex-col min-h-screen sm:bg-white-0.0 bg-white-1.0 h-full">
      <ToastContainer autoClose={TOAST_DURATION_MS} draggable={false} hideProgressBar />
      {!isStandaloneView && <Navbar />}
      <div className="h-full overflow-hidden">
        <Routes>
          <Route
            path="accounts/*"
            element={
              <PrivateRoute>
                <Accounts />
              </PrivateRoute>
            }
          />
          <Route
            path="call_rescue_monitor"
            element={
              <PrivateRoute>
                <CallRescueMonitor />
              </PrivateRoute>
            }
          />
          <Route
            path="wl_devices"
            element={
              <PrivateRoute>
                <WLDevices />
              </PrivateRoute>
            }
          />
          <Route
            path="provisioning_requests/*"
            element={
              <PrivateRoute>
                <ProvisioningRequests />
              </PrivateRoute>
            }
          />
          <Route
            path="user_profile"
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="tcr/*"
            element={
              <PrivateRoute>
                <Tcr />
              </PrivateRoute>
            }
          />
          <Route
            path="support"
            element={
              <PrivateRoute>
                <Support />
              </PrivateRoute>
            }
          />
          <Route
            path="survey_conversion"
            element={
              <PrivateRoute>
                <SurveyConversion />
              </PrivateRoute>
            }
          />
          {/* citas.pr talks to cosmos staff endpoints, which mirage does not mock. */}
          {!IS_MIRAGE && (
            <Route
              path="citasmedicas/*"
              element={
                <PrivateRoute>
                  <Citasmedicas />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="api"
            element={
              <PrivateRoute>
                <Api />
              </PrivateRoute>
            }
          />
          <Route
            path="product_updates/*"
            element={
              <PrivateRoute>
                <ProductUpdates />
              </PrivateRoute>
            }
          />
          <Route
            path="phone_settings/*"
            element={
              <PrivateRoute>
                <PhoneSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="phone-settings/*"
            element={
              <PrivateRoute>
                <PhoneSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Navigate to="accounts" />
              </PrivateRoute>
            }
          />
          <Route
            path="*"
            element={
              <PrivateRoute>
                <NotFound />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default AuthenticatedApp;
